import { useState, useEffect } from 'react';
import { getInitialData } from '../DatabaseAPI';
import { useAuth } from '../auth/AuthContext';
import { Button, Select } from '../components/ui';

function DebugLoginPage(props) {
    const [leagues, setLeagues] = useState([]);
    const [selectedLeagueId, setSelectedLeagueId] = useState(null);
    const [selectedLeagueName, setSelectedLeagueName] = useState(null);
    const [teamsForSelectedLeague, setTeamsForSelectedLeague] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [isButtonDisabled, setButtonDisabled] = useState(true);

    useEffect(() => {
      getInitialData()
        .then(data => {
          console.log('DebugLoginPage: Initial data received:', data);
          
          // Handle case where data or data.leagues is undefined
          const leagues = data?.leagues || [];
          setLeagues(leagues);
          
          // Set default selected league (first league)
          if (leagues && leagues.length > 0) {
            const firstLeague = leagues[0];
            setSelectedLeagueId(firstLeague.id);
            setSelectedLeagueName(firstLeague.name);
            // Set teams for the first league
            const teams = firstLeague.teams || [];
            setTeamsForSelectedLeague(teams);
            
            // Set default selected team (first team in first league)
            if (teams && teams.length > 0) {
              const firstTeam = teams[0];
              setSelectedTeam(firstTeam);
              props.setUser(firstTeam); // Pass full team object
            }
          } else {
            console.warn('DebugLoginPage: No leagues found in initial data:', data);
          }
          
          setButtonDisabled(false); // Enable the login button once data is loaded
        })
        .catch(error => {
          console.error('Error getting initial data:', error);
          // Set empty arrays to prevent crashes
          setLeagues([]);
          setTeamsForSelectedLeague([]);
          setButtonDisabled(false); // Still enable buttons so user can try other options
        });
    }, []);

    const handleLeagueSelectChange = (event) => {
      const newSelectedLeagueId = parseInt(event.target.value);
      setSelectedLeagueId(newSelectedLeagueId);
      
      // Find the selected league and update teams
      const selectedLeague = leagues.find(league => league.id === newSelectedLeagueId);
      if (selectedLeague) {
        setTeamsForSelectedLeague(selectedLeague.teams);
        setSelectedLeagueName(selectedLeague.name);
        // Set default team for this league (first team)
        if (selectedLeague.teams && selectedLeague.teams.length > 0) {
          const firstTeam = selectedLeague.teams[0];
          setSelectedTeam(firstTeam);
          props.setUser(firstTeam); // Pass full team object
        }
      }
    };

    const handleTeamSelectChange = (event) => {
      const selectedTeamId = parseInt(event.target.value);
      const selectedTeamObj = teamsForSelectedLeague.find(team => team.id === selectedTeamId);
      if (selectedTeamObj) {
        setSelectedTeam(selectedTeamObj);
        props.setUser(selectedTeamObj); // Pass full team object
      }
    };

    const handleLoginClick = () => {
        // NEW: Login button now goes to new UI team screen
        // Set the league ID in the parent component
        props.setLeagueId(selectedLeagueId);
        props.setLeagueName(selectedLeagueName);
        props.setUser(selectedTeam);
        
        // Launch NewApp instead of old app, pass selectedTeam and league data for routing logic
        if (props.setNewUIMode) {
            const leagueData = {
                id: selectedLeagueId,
                name: selectedLeagueName
            };
            props.setNewUIMode(true, selectedTeam, leagueData);
        }
    };


    const handleDebugUIClick = () => {
        // Set the league ID in the parent component
        props.setLeagueId(selectedLeagueId);
        props.setLeagueName(selectedLeagueName);
        
        // Launch DebugRouter
        if (props.setDebugMode) {
            props.setDebugMode(true);
        }
    };

    // Get logout function from AuthProvider
    const { logout } = useAuth();

    const handleLogoutClick = async () => {
        try {
            console.log('DebugLoginPage: Logout button clicked');
            await logout();
            // After logout, the AuthProvider will handle routing back to login screen
        } catch (error) {
            console.error('DebugLoginPage: Error during logout:', error);
        }
    };

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          minWidth: '400px'
        }}>
          <h2 style={{
            textAlign: 'center',
            marginBottom: '30px',
            color: '#2c3e50'
          }}>
            🐛 Debug Login
          </h2>
          
          <div style={{
            marginBottom: '20px'
          }}>
            <label htmlFor="league-select" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#495057'
            }}>
              Select League:
            </label>
            <Select
              id="league-select"
              value={selectedLeagueId?.toString() || ''}
              onChange={handleLeagueSelectChange}
              disabled={!leagues || leagues.length === 0}
              options={leagues?.map(league => ({
                value: league.id.toString(),
                label: league.name
              })) || []}
              fullWidth
              size="lg"
            />
          </div>
          
          <div style={{
            marginBottom: '30px'
          }}>
            <label htmlFor="team-select" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#495057'
            }}>
              Select Team:
            </label>
            <Select
              id="team-select"
              onChange={handleTeamSelectChange}
              disabled={!teamsForSelectedLeague || teamsForSelectedLeague.length === 0}
              options={teamsForSelectedLeague?.map(team => ({
                value: team.id.toString(),
                label: `${team.display_name}${team.packs > 0 ? ` (${team.packs} packs)` : ''}`
              })) || []}
              fullWidth
              size="lg"
            />
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {/* First row: Login (New UI) and Logout */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '10px'
            }}>
              <Button
                id="onLoginButton"
                variant="primary"
                size="lg"
                disabled={isButtonDisabled}
                onClick={handleLoginClick}
                style={{ flex: 1 }}
              >
                Login
              </Button>
              
              <Button
                id="logoutButton"
                variant="danger"
                size="lg"
                onClick={handleLogoutClick}
                style={{ flex: 1 }}
              >
                Logout
              </Button>
            </div>
            
            {/* Second row: Debug New UI */}
            <div>
              <Button
                id="debugUIButton"
                variant="secondary"
                size="lg"
                disabled={isButtonDisabled}
                onClick={handleDebugUIClick}
                fullWidth
              >
                🐛 Debug Component Viewer
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
}

export default DebugLoginPage;